import { type BusinessProfileFields, saveBusinessProfileInputSchema } from '@repo/api-contracts';
import { useRef, useState } from 'react';
import { useInvalidateBusinessProfile } from '@/entities/user';
import { trpc } from '@/shared/api';
import { getErrorMessage, mapServerValidationErrors } from '@/shared/lib';

const FIELD_KEYS = [
  'businessNumber',
  'companyName',
  'representativeName',
  'address',
  'businessType',
  'businessItem',
  'invoiceEmail',
  'contactName',
] as const;
export const useSaveBusinessProfile = () => {
  const mutation = trpc.users.saveBusinessProfile.useMutation();
  const invalidate = useInvalidateBusinessProfile();
  const lock = useRef(false);
  const [isPending, setPending] = useState(false);
  const [isSaved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof BusinessProfileFields, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const submit = async (values: BusinessProfileFields) => {
    if (lock.current) return;
    setSaved(false);
    setFormError(null);
    const parsed = saveBusinessProfileInputSchema.safeParse(values);
    if (!parsed.success) {
      const next: Partial<Record<keyof BusinessProfileFields, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof BusinessProfileFields;
        next[key] =
          key === 'businessNumber'
            ? '사업자등록번호는 숫자 10자리여야 합니다.'
            : key === 'invoiceEmail'
              ? '이메일을 올바르게 입력해 주세요.'
              : '입력값을 확인해 주세요.';
      }
      setErrors(next);
      return;
    }
    lock.current = true;
    setPending(true);
    setErrors({});
    try {
      await mutation.mutateAsync(parsed.data);
      await invalidate();
      setSaved(true);
    } catch (error) {
      const mapped = mapServerValidationErrors(error, FIELD_KEYS);
      if (mapped) {
        setErrors(mapped.fieldErrors);
        setFormError(mapped.formError);
      } else
        setFormError(
          getErrorMessage(error, '사업자 정보를 저장하지 못했습니다. 다시 시도해 주세요.'),
        );
    } finally {
      lock.current = false;
      setPending(false);
    }
  };
  return { submit, isPending, isSaved, errors, formError };
};
